import { stripHTML } from "@pathwright/blocks-core/src/text"
import { hasCompleteLikertResponses } from "@pathwright/blocks-core/src/blocks/likertscale/type/completion"
import { createID } from "@pathwright/blocks-engine"
import type {
  LikertScaleData,
  LikertScaleEditorStatement,
  LikertScaleOption,
  LikertScaleResponse,
  LikertScaleStatement
} from "./types"

export const MIN_SCALE_OPTIONS = 2
export const MAX_SCALE_OPTIONS = 7
export const MIN_NUMERIC_VALUE = -100
export const MAX_NUMERIC_VALUE = 100
export const SCALE_LABEL_MAX_LENGTH = 24

type LikertScaleOptionInput = Pick<LikertScaleOption, "label" | "order"> &
  Partial<Pick<LikertScaleOption, "numericValue" | "scaleOptionID">>

export const hasVisibleText = (value = ""): boolean => {
  return stripHTML(value).length > 0
}

export const createLikertScaleOption = ({
  order,
  label = "",
  numericValue = order
}: {
  order?: number
  label?: string
  numericValue?: number
} = {}): LikertScaleOption => ({
  label,
  numericValue: numericValue ?? 1,
  order: order ?? 1,
  scaleOptionID: createID()
})

export const createLikertStatement = ({
  order,
  label = ""
}: {
  order?: number
  label?: string
} = {}): LikertScaleStatement => ({
  label,
  order: order ?? 1,
  statementID: createID()
})

export const ensureIdentifiers = <T extends object>(
  items: T[] = [],
  idField: keyof T | string
): T[] => {
  return items.map((item) => {
    const itemRecord = item as Record<string, unknown>
    return itemRecord[String(idField)]
      ? item
      : ({ ...item, [idField]: createID() } as T)
  })
}

export const updateOrdering = <T extends { order: number }>(
  items: T[] = []
): T[] => {
  return items.map((item, index) => ({ ...item, order: index + 1 }))
}

const normalizeItems = <T extends { order: number; isNew?: boolean }>(
  items: T[] = [],
  idField: keyof T | string
): Omit<T, "isNew">[] =>
  updateOrdering(ensureIdentifiers(items, idField)).map((item) => {
    const { isNew, ...normalizedItem } = item
    return normalizedItem
  })

export const getEditableLikertScale = (
  scale: LikertScaleOptionInput[] | null = []
): LikertScaleOption[] => {
  return normalizeItems(scale || [], "scaleOptionID").map((option, index) => ({
    ...option,
    scaleOptionID: option.scaleOptionID || createID(),
    numericValue: Number.isInteger(option.numericValue)
      ? option.numericValue!
      : index + 1
  }))
}

export const clampNumericValue = (value: string | number): number => {
  const numberValue = Number.parseInt(String(value), 10)

  if (!Number.isFinite(numberValue)) return 0
  return Math.max(MIN_NUMERIC_VALUE, Math.min(MAX_NUMERIC_VALUE, numberValue))
}

export const getNextAvailableNumericValue = (
  scale: LikertScaleOption[],
  preferredValue = scale.length + 1
): number => {
  const usedValues = new Set(scale.map((option) => option.numericValue))
  const startingValue = clampNumericValue(preferredValue)

  for (
    let candidate = startingValue;
    candidate <= MAX_NUMERIC_VALUE;
    candidate += 1
  ) {
    if (!usedValues.has(candidate)) return candidate
  }

  for (
    let candidate = startingValue - 1;
    candidate >= MIN_NUMERIC_VALUE;
    candidate -= 1
  ) {
    if (!usedValues.has(candidate)) return candidate
  }

  return startingValue
}

export const getLikertScale = (
  scale: LikertScaleOption[] | null = []
): LikertScaleOption[] => {
  return getEditableLikertScale(scale).filter(
    (option) => option.scaleOptionID && Number.isInteger(option.numericValue)
  )
}

export const getLikertStatements = (
  statements: LikertScaleStatement[] | LikertScaleEditorStatement[] | null = []
): LikertScaleStatement[] => {
  return normalizeItems(statements || [], "statementID").filter((statement) =>
    hasVisibleText(statement.label)
  )
}

export const getLikertResponses = (
  data: Partial<LikertScaleData> = {}
): LikertScaleResponse[] => {
  const validStatementIDs = new Set(
    getLikertStatements(data.statements).map(
      (statement) => statement.statementID
    )
  )
  const validOptionIDs = new Set(
    getLikertScale(data.scale).map((option) => option.scaleOptionID)
  )
  const validResponses = (data.answer?.responses || []).filter(
    (response) =>
      response?.statementID &&
      response?.selectedScaleOptionID &&
      validStatementIDs.has(response.statementID) &&
      validOptionIDs.has(response.selectedScaleOptionID)
  )

  return validResponses.filter(
    (response, index, responses) =>
      responses.findIndex(
        (candidate) => candidate.statementID === response.statementID
      ) === index
  )
}

export const getAnsweredStatementCount = (
  data: Partial<LikertScaleData> = {}
): number => {
  return getLikertResponses(data).length
}

export const getLikertResponseMap = (
  data: Partial<LikertScaleData> = {}
): Map<string, string> => {
  return new Map(
    getLikertResponses(data).map((response) => [
      response.statementID,
      response.selectedScaleOptionID
    ])
  )
}

export const updateLikertResponse = ({
  data,
  statementID,
  selectedScaleOptionID,
  answeredDate = Date.now()
}: {
  data: LikertScaleData
  statementID: string
  selectedScaleOptionID: string
  answeredDate?: number
}): NonNullable<LikertScaleData["answer"]> => {
  const responses = (data.answer?.responses || []).filter(
    (response) => response?.statementID !== statementID
  )
  responses.push({
    statementID,
    selectedScaleOptionID
  })

  const hasSubmitted = hasCompleteLikertResponses({
    ...data,
    answer: { ...data.answer, responses }
  })

  return {
    ...data.answer,
    responses,
    hasSubmitted,
    answeredDate: hasSubmitted
      ? data.answer?.answeredDate ?? answeredDate
      : null
  }
}

export const buildEditorItems = ({
  items = [],
  min = 1,
  max = Number.POSITIVE_INFINITY,
  createItem,
  getText,
  idField
}: {
  items?: LikertScaleStatement[] | null
  min?: number
  max?: number
  createItem: (args: { order: number }) => LikertScaleStatement
  getText: (item: LikertScaleStatement) => string
  idField: keyof LikertScaleStatement
}): LikertScaleEditorStatement[] => {
  const persistedItems = normalizeItems(items || [], idField).filter((item) =>
    hasVisibleText(getText(item))
  )
  const editorItems: LikertScaleEditorStatement[] = [...persistedItems]
  const minBlanks = Math.max(min - persistedItems.length, 0)
  const extraBlank =
    persistedItems.length >= min && persistedItems.length < max ? 1 : 0

  Array.from({ length: minBlanks + extraBlank }).forEach(() => {
    editorItems.push({
      ...createItem({ order: editorItems.length + 1 }),
      isNew: true
    })
  })

  return updateOrdering(editorItems)
}

export const getNewItemIndices = <T extends { isNew?: boolean }>(
  items: T[] = []
): number[] => {
  return items.reduce<number[]>((indices, item, index) => {
    if (item.isNew) {
      indices.push(index)
    }

    return indices
  }, [])
}
